"use client"

import { useState, useEffect, useCallback, useMemo } from "react"
import { useAuth } from "./use-auth"

export interface SystemSetting {
  id: number
  key: string
  value: string
  description: string
  created_at: string
  updated_at: string
}

// Normalize any unknown payload into a clean SystemSetting[]
function normalizeSettingsPayload(payload: any): SystemSetting[] {
  let arr: any[] = []

  // Common shapes:
  // - { settings: SystemSetting[] }
  // - SystemSetting[]
  // - { [key: string]: string | number | null }
  if (Array.isArray(payload?.settings)) {
    arr = payload.settings
  } else if (Array.isArray(payload)) {
    arr = payload
  } else if (payload && typeof payload === "object") {
    // Convert object map to array of settings rows
    arr = Object.entries(payload).map(([key, value]) => ({
      id: -1,
      key,
      value: String(value ?? ""),
      description: "",
      created_at: "",
      updated_at: "",
    }))
  }

  // Filter out invalid entries and coerce types
  return arr
    .filter(Boolean)
    .filter((item) => typeof item === "object" && item !== null && typeof item.key === "string")
    .map((item) => ({
      id: typeof item.id === "number" ? item.id : -1,
      key: String(item.key),
      value: String(item.value ?? ""),
      description: typeof item.description === "string" ? item.description : "",
      created_at: typeof item.created_at === "string" ? item.created_at : "",
      updated_at: typeof item.updated_at === "string" ? item.updated_at : "",
    }))
}

export function useSystemSettings() {
  const { fetchWithAuth, isAuthenticated, user } = useAuth()
  const userRole = user?.role
  const [settings, setSettings] = useState<SystemSetting[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Load settings from database
  const loadSettings = useCallback(async () => {
    if (!isAuthenticated || userRole !== "super_user") {
      setSettings([])
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      const response = await fetchWithAuth("/api/system/settings", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      })

      if (!response.ok) {
        try {
          const err = await response.json()
          console.error("Failed to load system settings:", err)
        } catch {
          console.error("Failed to load system settings. Status:", response.status)
        }
        setSettings([])
        return
      }

      const data = await response.json()
      const normalized = normalizeSettingsPayload(data)
      setSettings(normalized)
    } catch (error) {
      console.error("Error loading system settings:", error)
      setSettings([])
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth, isAuthenticated, userRole])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const updateSetting = useCallback(async (key: string, value: string) => {
    if (!isAuthenticated) {
      throw new Error("User not authenticated")
    }

    if (userRole !== "super_user") {
      throw new Error("Only super users can modify system settings")
    }

    try {
      const response = await fetchWithAuth("/api/system/settings", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ key, value }),
      })

      if (!response.ok) {
        let message = "Failed to update setting"
        try {
          const errorData = await response.json()
          message = errorData?.error || message
        } catch {
          // ignore
        }
        throw new Error(message)
      }

      const data = await response.json()
      const raw = data?.setting ?? data
      const [normalized] = normalizeSettingsPayload([raw])

      if (!normalized) {
        const fallback: SystemSetting = {
          id: -1,
          key,
          value: String(value ?? ""),
          description: "",
          created_at: "",
          updated_at: "",
        }

        setSettings((prev) => {
          const safePrev = Array.isArray(prev)
            ? prev.filter((it) => it && typeof it === "object" && typeof (it as any).key === "string")
            : []
          const idx = safePrev.findIndex((s) => s.key === key)
          if (idx >= 0) {
            const copy = [...safePrev]
            copy[idx] = fallback
            return copy
          }
          return [...safePrev, fallback]
        })

        return fallback
      }

      setSettings((prev) => {
        const safePrev = Array.isArray(prev)
          ? prev.filter((it) => it && typeof it === "object" && typeof (it as any).key === "string")
          : []
        const idx = safePrev.findIndex((s) => s.key === normalized.key)
        if (idx >= 0) {
          const copy = [...safePrev]
          copy[idx] = normalized
          return copy
        }
        return [...safePrev, normalized]
      })

      return normalized
    } catch (error) {
      console.error("Error updating setting:", error)
      throw error
    }
  }, [fetchWithAuth, isAuthenticated, userRole])

  const getSetting = useCallback(
    (key: string) => {
      const list = Array.isArray(settings)
        ? settings.filter((it) => it && typeof it === "object" && typeof (it as any).key === "string")
        : []
      return list.find((setting) => setting.key === key)
    },
    [settings],
  )

  const getSettingValue = useCallback(
    (key: string, defaultValue = "") => {
      const hit = getSetting(key)
      return hit?.value ?? defaultValue
    },
    [getSetting],
  )

  const settingsMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const s of settings) {
      if (s && typeof s.key === "string") {
        map.set(s.key, s.value ?? "")
      }
    }
    return map
  }, [settings])

  return {
    settings,
    settingsMap,
    isLoading,
    updateSetting,
    getSetting,
    getSettingValue,
    refreshSettings: loadSettings,
  }
}
